import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Sparkles, CheckCircle2, ChevronRight, Zap, Bot, CreditCard,
  FileText, ExternalLink, MessageSquare, ShieldCheck, Layers, Cpu, Server,
  Clock, Lock, DollarSign, Headset, ChevronDown, ChevronUp, Send,
  Search, Code2
} from 'lucide-react';

import Navigation from '../components/Navigation';
import ThreeBackground from '../components/ThreeBackground';
import Modal from '../components/Modal';
import NexaLogoIcon from '../components/NexaLogoIcon';

import SmartAssistant from '../components/SmartAssistant';
import BackgroundRemover from '../components/BackgroundRemover';
import AIImageEditor from '../components/AIImageEditor';

import MetaSEO from '../components/MetaSEO';
import logo from "../images/logo.png";
import './Home.css';

const Home = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState({ title: '', component: null });
  const [openFaqIndex, setOpenFaqIndex] = useState(0);

  // Convobix Demo Chat inside section
  const [chatMessages, setChatMessages] = useState([
    {
      sender: 'ai',
      text: "Hi there! I'm Convobix AI Sales Agent. What kind of business are you running today?"
    }
  ]);
  const [inputVal, setInputVal] = useState('');
  const [isAiTyping, setIsAiTyping] = useState(false);

  const openModal = (title, component) => {
    setModalContent({ title, component });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setTimeout(() => setModalContent({ title: '', component: null }), 300);
  };

  const handleSendChat = (e) => {
    e.preventDefault();
    if (!inputVal.trim()) return;

    const userMsg = inputVal.trim();
    setChatMessages((prev) => [...prev, { sender: 'user', text: userMsg }]);
    setInputVal('');
    setIsAiTyping(true);

    setTimeout(() => {
      setIsAiTyping(false);
      let replyText = "Awesome! Convobix automatically qualifies your visitors, generates itemized PDF quotes, and accepts Stripe payments directly inside chat.";
      if (userMsg.toLowerCase().includes('price') || userMsg.toLowerCase().includes('cost')) {
        replyText = "Convobix plans start at $19/mo with full Stripe checkout & PDF quoting. Would you like me to generate a demo checkout link?";
      }
      setChatMessages((prev) => [
        ...prev,
        { sender: 'ai', text: replyText, hasAction: true }
      ]);
    }, 900);
  };

  const services = [
    {
      id: '01',
      tag: 'AI & NEURAL AGENTS',
      title: 'Custom AI Architecture & Agents',
      description: 'Enterprise fine-tuned LLM workflows, autonomous sales bots, recommendation engines, and client-side vector search modules.',
      metric: 'Sub-20ms Latency',
      techs: ['Fine-Tuned LLMs', 'Vector Search', 'Custom Agents']
    },
    {
      id: '02',
      tag: 'WEB PLATFORMS',
      title: 'High-Performance Web Platforms',
      description: 'Ultra-fast web applications built with React 18, Next.js, and bespoke modern design systems with 60fps micro-animations.',
      metric: '100/100 Lighthouse',
      techs: ['React 18 & Next.js', 'Glassmorphism UI', 'PWA Ready']
    },
    {
      id: '03',
      tag: 'MOBILE APPS',
      title: 'Cross-Platform Mobile Apps',
      description: 'Native-feel iOS and Android applications focused on sleek ergonomics, instant responsiveness, and offline readiness.',
      metric: 'Native 60FPS UI',
      techs: ['iOS & Android', 'Offline Sync Engine', 'Biometrics & Push']
    },
    {
      id: '04',
      tag: 'CLOUD & API',
      title: 'Cloud Infrastructure & API Systems',
      description: 'Scalable serverless microservices, secure database orchestration, and automated CI/CD deployment pipelines.',
      metric: '99.99% SLA Uptime',
      techs: ['AWS & Docker', 'GraphQL & REST', 'Automated CI/CD']
    }
  ];

  const featuredTools = [
    {
      icon: <Cpu className="tool-icon-svg orange" />,
      title: 'Smart Shopping Assistant',
      description: 'AI recommendation engine finding top-tier purchasing sources in real time.',
      component: <SmartAssistant />
    },
    {
      icon: <Layers className="tool-icon-svg orange" />,
      title: 'AI Background Remover',
      description: 'Browser-based background extraction algorithm powered by AI computer vision.',
      component: <BackgroundRemover />
    },
    {
      icon: <Sparkles className="tool-icon-svg orange" />,
      title: 'AI Image Studio & Filters',
      description: 'Neural image manipulation, color matrix tweaks, and visual enhancements.',
      component: <AIImageEditor />
    }
  ];

  const stats = [
    { number: '150+', label: 'Enterprise Projects Delivered' },
    { number: '99.8%', label: 'Client Satisfaction Rate' },
    { number: '< 15ms', label: 'Client AI Latency' },
    { number: '24/7', label: 'Dedicated Tech Support' }
  ];

  const pillars = [
    {
      icon: <Clock size={28} className="orange-text" />,
      title: '14-Day Rapid Delivery',
      desc: 'Agile development sprints taking your concept from initial specification to live production deployment in weeks.'
    },
    {
      icon: <Lock size={28} className="orange-text" />,
      title: 'Enterprise Security & Privacy',
      desc: 'On-premise model fine-tuning, client-side vector search, and zero data leakage for corporate IP safety.'
    },
    {
      icon: <DollarSign size={28} className="orange-text" />,
      title: 'Transparent Fixed Pricing',
      desc: 'Milestone-based project scoping with zero surprise bills, hidden platform charges, or bloated hourly rates.'
    },
    {
      icon: <Headset size={28} className="orange-text" />,
      title: 'Dedicated 24/7 SLA Support',
      desc: 'Post-launch engineering support, continuous security patches, performance tuning, and scalability management.'
    }
  ];

  const pipelineSteps = [
    {
      step: '01',
      icon: <Search className="orange-text" size={24} />,
      title: 'Discovery & Architecture Blueprint',
      desc: 'We map out product requirements, user journeys, data schemas, and technical system specifications.'
    },
    {
      step: '02',
      icon: <Cpu className="orange-text" size={24} />,
      title: 'AI Model Tuning & UI Prototyping',
      desc: 'Custom LLM prompt engineering, dataset preparation, and pixel-perfect interactive prototype design.'
    },
    {
      step: '03',
      icon: <Code2 className="orange-text" size={24} />,
      title: 'High-Speed Full-Stack Build',
      desc: 'Production coding in React 18, Next.js, Python microservices, and 60fps responsive animation integration.'
    },
    {
      step: '04',
      icon: <ShieldCheck className="orange-text" size={24} />,
      title: 'Security Audit, Launch & SLA Growth',
      desc: 'Stress testing, vulnerability scanning, cloud CI/CD deployment, and 24/7 continuous optimization.'
    }
  ];

  const faqs = [
    {
      q: "What types of AI and Web applications do you build?",
      a: "We specialize in custom enterprise AI assistants, autonomous sales agents, SaaS platforms, client-side AI utilities, high-performance web applications, and mobile apps."
    },
    {
      q: "How long does a typical custom software project take?",
      a: "Our rapid delivery model typically delivers initial MVP prototypes in 7-14 days, with full production launches taking 3-6 weeks depending on project complexity."
    },
    {
      q: "Can you integrate AI into our existing web platform or mobile app?",
      a: "Yes! We can seamlessly integrate OpenAI, Claude, Llama 3, or custom fine-tuned models into your existing stack via clean REST or GraphQL APIs."
    },
    {
      q: "Do you provide post-launch maintenance and SLA support?",
      a: "Absolutely. Every project includes 30 days of complimentary post-launch monitoring, followed by optional ongoing SLA support plans."
    }
  ];

  const technologies = [
    'React 18', 'Next.js', 'TypeScript', 'Node.js', 'Python', 'PyTorch',
    'TensorFlow', 'Three.js', 'AWS Cloud', 'Docker', 'GraphQL', 'Vite'
  ];

  return (
    <div className="home light-theme">
      <MetaSEO 
        title="Enterprise AI Systems & Web Development Agency"
        description="NexaAI Studio architects high-performance AI agents, autonomous sales software like Convobix, modern React/Next.js web platforms, and interactive developer tools."
        keywords="NexaAI Studio, Convobix, AI sales agent, AI software agency, web development, React 18, LLM fine-tuning, vector database, developer tools, background remover"
      />
      <ThreeBackground />
      <Navigation />

      {/* Top Banner Tagline */}
      <div className="hero-announcement">
        <div className="container">
          <div className="announcement-pill">
            <span className="pill-pulse"></span>
            <span className="pill-text">NEXA AI STUDIO 2.0</span>
            <span className="pill-divider">•</span>
            <Link to="/tools" className="pill-link">
              Explore Tools Hub <ChevronRight size={14} />
            </Link>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="container">
          <div className="hero-main-content">
            <h1 className="hero-headline">
              Architecting Next-Gen <br />
              <span className="orange-gradient-text">Intelligence & Web Systems</span>
            </h1>

            <p className="hero-description">
              We design and build high-performance AI software, enterprise web platforms, and custom developer tools engineered for scalable growth.
            </p>

            <div className="hero-cta-group">
              <Link to="/contact" className="btn btn-primary btn-large">
                <span>Start Your Project</span>
                <ArrowRight size={18} />
              </Link>
              <Link to="/tools" className="btn btn-dark btn-large">
                <span>Explore Tools Hub</span>
                <ChevronRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section-light">
        <div className="container">
          <div className="stats-grid-light">
            {stats.map((stat, index) => (
              <div key={index} className="stat-card-light">
                <div className="stat-num">{stat.number}</div>
                <div className="stat-desc">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DEDICATED SPOTLIGHT SECTION: CONVOBIX FLAGSHIP AI PRODUCT */}
      <section className="convobix-spotlight-section" id="convobix">
        <div className="container">
          <div className="spotlight-card">
            <div className="spotlight-grid">
              {/* Left Content */}
              <div className="spotlight-text">
                <div className="spotlight-badge">
                  <Sparkles size={14} className="orange-text" />
                  FLAGSHIP AI PRODUCT
                </div>

                <h2 className="spotlight-title">
                  Convobix — <span className="orange-gradient-text">24/7 Autonomous AI Sales Representative</span>
                </h2>

                <p className="spotlight-desc">
                  Turn website visitors into paying clients on auto-pilot. Convobix isn't just a chatbot — it qualifies high-value leads, recommends products, generates instant PDF quotes, takes Stripe payments, and closes deals 24/7 across Web, WhatsApp, and Instagram.
                </p>

                <div className="spotlight-features-list">
                  <div className="s-feat-item">
                    <CheckCircle2 size={18} className="orange-text" />
                    <span>Real-Time Lead Qualification & Budget Scoring</span>
                  </div>
                  <div className="s-feat-item">
                    <CheckCircle2 size={18} className="orange-text" />
                    <span>In-Chat Stripe Checkout & Instant Payments</span>
                  </div>
                  <div className="s-feat-item">
                    <CheckCircle2 size={18} className="orange-text" />
                    <span>Automated Itemized PDF Quote Generation</span>
                  </div>
                  <div className="s-feat-item">
                    <CheckCircle2 size={18} className="orange-text" />
                    <span>Omnichannel Sync (Web, WhatsApp & Instagram DMs)</span>
                  </div>
                  <div className="s-feat-item">
                    <CheckCircle2 size={18} className="orange-text" />
                    <span>Smart Exit-Intent & Proactive Discount Triggers</span>
                  </div>
                </div>

                <div className="spotlight-actions">
                  <a
                    href="https://panel.convobix.com"
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-primary"
                  >
                    <span>Test Convobix Demo</span>
                    <ArrowRight size={16} />
                  </a>
                  <a
                    href="https://convobix.com"
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-secondary"
                  >
                    <span>Visit Convobix.com</span>
                    <ExternalLink size={16} />
                  </a>
                </div>
              </div>

              {/* Right Live Chat Preview */}
              <div className="spotlight-preview">
                <div className="convobix-chat-card">
                  <div className="c-head">
                    <div className="c-avatar">⚡</div>
                    <div>
                      <h4 className="c-name">Convobix AI Sales Agent</h4>
                      <span className="c-status">Active · 24/7 Sales Mode</span>
                    </div>
                  </div>

                  <div className="c-body">
                    {chatMessages.map((msg, idx) => (
                      <div key={idx} className={`c-bubble ${msg.sender}`}>
                        <p>{msg.text}</p>
                        {msg.hasAction && (
                          <div className="c-actions">
                            <button className="c-btn primary" onClick={() => alert("Simulating Stripe Payment Link ($49.00)...")}>
                              💳 Pay via Stripe ($49.00)
                            </button>
                            <button className="c-btn secondary" onClick={() => alert("Downloading PDF Quote Document...")}>
                              📄 Download PDF Quote
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                    {isAiTyping && (
                      <div className="c-bubble ai typing">
                        <span>Convobix AI is processing...</span>
                      </div>
                    )}
                  </div>

                  <form onSubmit={handleSendChat} className="c-input-bar">
                    <input
                      type="text"
                      placeholder="Ask Convobix AI sales agent..."
                      value={inputVal}
                      onChange={(e) => setInputVal(e.target.value)}
                    />
                    <button type="submit" className="c-send">
                      <Send size={15} />
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VALUE PILLARS SECTION */}
      <section className="pillars-section">
        <div className="container">
          <div className="section-head">
            <span className="head-badge">WHY CHOOSE NEXAAI STUDIO</span>
            <h2 className="head-title">Engineered for Reliability & Speed</h2>
            <p className="head-subtitle">
              We combine enterprise technical precision with agile product execution to deliver measurable software outcomes.
            </p>
          </div>

          <div className="pillars-grid">
            {pillars.map((pillar, idx) => (
              <div key={idx} className="pillar-card">
                <div className="pillar-icon-box">{pillar.icon}</div>
                <h3 className="pillar-title">{pillar.title}</h3>
                <p className="pillar-desc">{pillar.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Enterprise Services Section (MINIMALIST STRIPE-STYLE ROW MATRIX - NO CARDS, NO ICONS) */}
      <section className="services-matrix-section" id="services">
        <div className="container">
          <div className="section-head">
            <span className="head-badge">EXPERT CAPABILITIES</span>
            <h2 className="head-title">Our Enterprise Services</h2>
            <p className="head-subtitle">
              Comprehensive engineering for ambitious digital transformations
            </p>
          </div>

          <div className="services-matrix-list">
            {services.map((service, index) => (
              <div key={index} className="service-matrix-row">
                <div className="matrix-col-num">{service.id}</div>
                
                <div className="matrix-col-main">
                  <div className="matrix-title-group">
                    <span className="matrix-tag">{service.tag}</span>
                    <h3 className="matrix-title">{service.title}</h3>
                  </div>
                  <p className="matrix-desc">{service.description}</p>
                </div>

                <div className="matrix-col-techs">
                  {service.techs.map((t, idx) => (
                    <span key={idx} className="matrix-tech-chip">{t}</span>
                  ))}
                </div>

                <div className="matrix-col-action">
                  <span className="matrix-metric">{service.metric}</span>
                  <Link to="/contact" className="matrix-arrow-link" title="Explore Service">
                    <ArrowRight size={18} />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SLEEK TIMELINE WORKFLOW SECTION */}
      <section className="pipeline-section">
        <div className="container">
          <div className="section-head">
            <span className="head-badge">PRODUCTION WORKFLOW</span>
            <h2 className="head-title">Our 4-Step Production Pipeline</h2>
            <p className="head-subtitle">
              From concept validation to high-availability deployment in record time.
            </p>
          </div>

          <div className="timeline-pipeline-wrapper">
            <div className="timeline-connecting-line"></div>
            <div className="timeline-pipeline-grid">
              {pipelineSteps.map((step, idx) => (
                <div key={idx} className="timeline-step-card">
                  <div className="timeline-node">
                    <div className="timeline-badge-icon">{step.icon}</div>
                    <span className="timeline-number-tag">{step.step}</span>
                  </div>
                  <div className="timeline-card-body">
                    <h3 className="p-step-title">{step.title}</h3>
                    <p className="p-step-desc">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Featured Developer Utilities */}
      <section className="featured-tools-section">
        <div className="container">
          <div className="section-head">
            <span className="head-badge">FREE DEVELOPER SUITE</span>
            <h2 className="head-title">Interactive AI Tools Hub</h2>
            <p className="head-subtitle">
              Launch our free interactive web tools directly or visit the Tools Hub page for all 10+ utilities.
            </p>
          </div>

          <div className="tools-cards-grid">
            {featuredTools.map((tool, index) => (
              <div key={index} className="tool-light-card" onClick={() => openModal(tool.title, tool.component)}>
                <div className="card-top-row">
                  <div className="icon-box">{tool.icon}</div>
                  <span className="card-badge">Instant Run</span>
                </div>
                <h3 className="card-heading">{tool.title}</h3>
                <p className="card-text">{tool.description}</p>
                <div className="card-cta">
                  <span>Launch Tool</span>
                  <ArrowRight size={16} />
                </div>
              </div>
            ))}

            {/* Hub Teaser Card */}
            <Link to="/tools" className="tool-light-card dark-teaser">
              <div className="teaser-inner">
                <div className="icon-box orange-bg">
                  <Sparkles size={24} color="#ffffff" />
                </div>
                <h3 className="card-heading text-white">Full Tools Hub</h3>
                <p className="card-text text-gray">
                  Explore 10+ developer suites including Password Generator, QR Code Engine, Diff Checker & Color Extractors.
                </p>
                <div className="card-cta text-orange">
                  <span>Go to Tools Hub Page</span>
                  <ArrowRight size={18} />
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section className="technologies-section-light">
        <div className="container">
          <div className="section-head">
            <span className="head-badge">TECH STACK</span>
            <h2 className="head-title">Technologies We Master</h2>
          </div>

          <div className="tech-pills-grid">
            {technologies.map((tech, idx) => (
              <span key={idx} className="t-pill">
                <CheckCircle2 size={14} className="orange-text" />
                <span>{tech}</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Accordion Section */}
      <section className="home-faq-section">
        <div className="container">
          <div className="section-head">
            <span className="head-badge">FREQUENTLY ASKED QUESTIONS</span>
            <h2 className="head-title">Got Questions? We Have Answers.</h2>
          </div>

          <div className="home-faq-box">
            {faqs.map((faq, idx) => (
              <div
                key={idx}
                className={`h-faq-item ${openFaqIndex === idx ? 'open' : ''}`}
                onClick={() => setOpenFaqIndex(openFaqIndex === idx ? -1 : idx)}
              >
                <div className="h-faq-q">
                  <h3>{faq.q}</h3>
                  <span className="h-faq-icon">
                    {openFaqIndex === idx ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                  </span>
                </div>
                {openFaqIndex === idx && (
                  <div className="h-faq-a">
                    <p>{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Banner */}
      <section className="cta-banner-section">
        <div className="container">
          <div className="cta-banner-card">
            <div className="banner-content">
              <h2 className="banner-title">Ready to Build Your Breakthrough Product?</h2>
              <p className="banner-subtitle">
                Schedule a consultation with our AI & web development team today.
              </p>
              <Link to="/contact" className="btn btn-primary btn-large">
                <span>Start Conversation</span>
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Shared Modal */}
      <Modal isOpen={modalOpen} onClose={closeModal} title={modalContent.title}>
        {modalContent.component}
      </Modal>

      {/* Footer */}
      <footer className="footer-light">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <Link to="/" className="logo">
                <NexaLogoIcon size={38} />
                <div className="logo-text">
                  <span className="logo-main">Nexa<span className="orange-text">AI</span></span>
                  <span className="logo-sub">STUDIO</span>
                </div>
              </Link>
              <p className="footer-tagline">Architecting high-performance digital tools and AI systems.</p>
            </div>
            <div className="footer-links">
              <Link to="/">Home</Link>
              <Link to="/tools">Tools Hub</Link>
              <Link to="/contact">Contact</Link>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; {new Date().getFullYear()} NexaAI Studio. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
